import { createReverbImpulse, audioBufferToWav, audioBufferToMp3, detectBPM } from "../utils/audioUtils";

import djKahnuTag from "../../../assets/audio/dj-kahnu.mp3";
import djSrikantTag from "../../../assets/audio/dj-srikant.mp3";

/**
 * DJ vocal tag sources.
 *
 * These are the only audio files in the sampler — every other pad is synthesized. Real
 * recordings are used here because oscillator formant synthesis cannot make a specific
 * name reliably intelligible; a name that is not clearly recognisable defeats the point
 * of a vocal tag. They are still decoded into AudioBuffers and played through the Web
 * Audio graph, so they route through the sampler bus and land in recorded mixes exactly
 * like the synthesized pads.
 */
const VOCAL_TAGS = {
  kahnu: { url: djKahnuTag, label: "DJ Kahnu" },
  srikant: { url: djSrikantTag, label: "DJ Srikant" }
};

/**
 * DJAudioEngine
 * Core audio processor using Web Audio API for mixing, effects, scratching, microphone, and sampler.
 */
export class DJAudioEngine {
  constructor() {
    this.audioContext = null;
    this.masterGainNode = null;
    this.masterCompressorNode = null;
    this.masterAnalyserNode = null;
    
    // Crossfader nodes
    this.crossfadeLeftGain = null;
    this.crossfadeRightGain = null;
    this.crossfaderValue = 0; // -1 (Left) to +1 (Right)

    // Decks Map: id -> DeckInstance
    this.decks = new Map();
    
    // Microphone Nodes
    this.micStream = null;
    this.micSourceNode = null;
    this.micGainNode = null;
    this.micAnalyserNode = null;
    this.micHighpassNode = null;
    this.micFeedbackNotchNode = null;
    this.micHighDampNode = null;
    this.micEqLow = null;
    this.micEqMid = null;
    this.micEqHigh = null;
    this.micVoiceFxNode = null; // Can hold waveshaper, delay, or modulator nodes
    this.micVoiceFxType = "clean"; // clean, echo, reverb, megaphone, radio, robot, deep, chipmunk
    this.micActiveOscillators = [];
    this.micEchoDelayTime = 0.65; // 650ms default delay time for clean DJ phrase loops ("Dj Kahnu")
    this.micEchoRepeats = 3;      // 3 clean repeats: "Dj Kahnu", "Dj Kahnu", "Dj Kahnu"
    this.micEchoDelayNode = null;
    this.micEchoFeedbackNode = null;
    this.micEchoFilterNode = null;
    this.isMicActive = false;
    this.autoDucking = false;
    this.duckingFactor = 0.3; // Reduce music to 30% when speaking

    // Input capture mode:
    //  "raw"     -> no browser voice DSP. Full unbroken sentences, full bandwidth.
    //               Requires headphones, otherwise the open mic can howl.
    //  "speaker" -> browser echo cancellation + noise suppression + anti-feedback
    //               filters. Survives laptop-speaker monitoring, but the browser DSP
    //               treats your own monitored voice as echo and chops syllables.
    this.micInputMode = "raw";
    this.micFeedbackGuardNode = null; // auto-attenuates the monitor path if it starts howling
    this.micFeedbackGuardActive = false;
    this.onMicFeedbackGuard = null;   // optional UI callback(isDucking)

    // Input health / recovery. The OS can revoke a capture device at any time
    // (headset unplugged, another app grabs it, machine sleeps), which silently
    // kills the stream. These track that and drive auto-recovery.
    this.micRequestedDeviceId = null;
    this.micActiveDeviceFellBack = false;
    this.micHealth = "idle";          // idle | live | recovering | lost
    this.onMicHealthChange = null;    // optional UI callback(health, detail)
    this.micRecoverAttempts = 0;
    this.micRecoverTimer = null;
    this.micStallFrames = 0;
    this.deviceChangeHandler = null;
    this.contextStateHandler = null;

    // Sampler Nodes
    this.samplerGainNode = null;

    // Decoded vocal tag AudioBuffers, keyed by VOCAL_TAGS key, plus in-flight loads
    // so concurrent presses share one fetch instead of racing.
    this.vocalTagBuffers = {};
    this.vocalTagLoads = {};

    // Recording state
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this.isRecording = false;
    this.recordingStartTime = 0;
    this.recordingElapsedTime = 0;
    this.recordingInterval = null;
    this.recordingBitrate = 192;           // MP3 kbps for exported mixes
    this.onRecordingEncodeProgress = null; // optional UI callback(percent)

    // Shared Reverb impulse response buffer
    this.reverbImpulseBuffer = null;
  }

  /**
   * Initialize main AudioContext and Master channels
   */
  initialize() {
    // A closed context can never be reopened — Web Audio has no reopen. Treat it as
    // absent and build a fresh one, otherwise the engine stays permanently dead after
    // a destroy()/close() (page remount, hot reload) with no way back.
    if (this.audioContext && this.audioContext.state === "closed") {
      this.audioContext = null;
    }
    if (this.audioContext) return;

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    this.audioContext = new AudioContextClass({ latencyHint: "interactive" });

    const ctx = this.audioContext;

    // Master Compressor (limiter + glue compressor)
    this.masterCompressorNode = ctx.createDynamicsCompressor();
    this.masterCompressorNode.threshold.value = -1.5; // dB
    this.masterCompressorNode.knee.value = 8;
    this.masterCompressorNode.ratio.value = 12.0;
    this.masterCompressorNode.attack.value = 0.005; // 5ms
    this.masterCompressorNode.release.value = 0.080; // 80ms

    // Master Gain
    this.masterGainNode = ctx.createGain();
    this.masterGainNode.gain.value = 0.8; // Default 80% volume

    // Master Analyser
    this.masterAnalyserNode = ctx.createAnalyser();
    this.masterAnalyserNode.fftSize = 512;

    // Crossfader Gain Nodes
    this.crossfadeLeftGain = ctx.createGain();
    this.crossfadeRightGain = ctx.createGain();
    this.setCrossfader(0); // Center by default

    // Connect Crossfader Gains to Master Compressor
    this.crossfadeLeftGain.connect(this.masterCompressorNode);
    this.crossfadeRightGain.connect(this.masterCompressorNode);

    // Master path: Compressor -> Master Gain -> Master Analyser -> Speakers
    this.masterCompressorNode.connect(this.masterGainNode);
    this.masterGainNode.connect(this.masterAnalyserNode);
    this.masterAnalyserNode.connect(ctx.destination);

    // Sampler Gain
    this.samplerGainNode = ctx.createGain();
    this.samplerGainNode.gain.value = 0.7;
    this.samplerGainNode.connect(this.masterCompressorNode);

    // Generate shared reverb impulse response
    this.reverbImpulseBuffer = createReverbImpulse(ctx, 2.0);

    // Decode the vocal tags in the background so the first pad press fires instantly
    this.preloadVocalTags();
  }

  /**
   * Start resume safety for browsers
   */
  async ensureContextActive() {
    this.initialize();
    if (this.audioContext && this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }
  }

  /**
   * Set Master volume (0 to 1)
   */
  setMasterVolume(value) {
    if (!this.masterGainNode) return;
    this.masterGainNode.gain.setTargetAtTime(value, this.audioContext.currentTime, 0.01);
  }

  /**
   * Set Crossfader position (-1 to 1)
   */
  setCrossfader(value) {
    this.crossfaderValue = value;
    if (!this.crossfadeLeftGain || !this.crossfadeRightGain) return;

    // Equal power curve crossfade:
    // Left channel gain = cos(pi/4 * (value + 1))
    // Right channel gain = sin(pi/4 * (value + 1))
    const leftGain = Math.cos(((value + 1) * Math.PI) / 4);
    const rightGain = Math.sin(((value + 1) * Math.PI) / 4);

    const time = this.audioContext ? this.audioContext.currentTime : 0;
    this.crossfadeLeftGain.gain.setTargetAtTime(leftGain, time, 0.01);
    this.crossfadeRightGain.gain.setTargetAtTime(rightGain, time, 0.01);
  }

  /**
   * Add a Deck to the system
   */
  createDeck(id, crossfadeSide = "left") {
    this.initialize();
    const ctx = this.audioContext;

    // Clean up if deck already exists
    if (this.decks.has(id)) {
      this.destroyDeck(id);
    }

    // Create deck processing chain
    const analyserNode = ctx.createAnalyser();
    analyserNode.fftSize = 256;

    const eqLowNode = ctx.createBiquadFilter();
    eqLowNode.type = "lowshelf";
    eqLowNode.frequency.value = 250; // Bass

    const eqMidNode = ctx.createBiquadFilter();
    eqMidNode.type = "peaking";
    eqMidNode.Q.value = 1.0;
    eqMidNode.frequency.value = 1200; // Mids

    const eqHighNode = ctx.createBiquadFilter();
    eqHighNode.type = "highshelf";
    eqHighNode.frequency.value = 4000; // Highs

    // Filter Node (Combination of LP/HP)
    const filterNode = ctx.createBiquadFilter();
    filterNode.type = "lowpass";
    filterNode.frequency.value = 20000; // Initially open

    // Gain Nodes
    const deckGainNode = ctx.createGain();
    deckGainNode.gain.value = 0.8; // Channel fader

    const pannerNode = ctx.createStereoPanner();
    pannerNode.pan.value = 0; // Balance

    // Delay FX chain
    const delayNode = ctx.createDelay(1.5);
    delayNode.delayTime.value = 0.3; // 300ms
    const delayFeedbackNode = ctx.createGain();
    delayFeedbackNode.gain.value = 0.4;
    const delayDryWetNode = ctx.createGain();
    delayDryWetNode.gain.value = 0; // Bypassed

    // Reverb FX chain
    const reverbNode = ctx.createConvolver();
    reverbNode.buffer = this.reverbImpulseBuffer;
    const reverbDryWetNode = ctx.createGain();
    reverbDryWetNode.gain.value = 0; // Bypassed

    // Distortion node
    const distortionNode = ctx.createWaveShaper();
    distortionNode.curve = null; // Bypassed
    distortionNode.oversample = "4x";
    const distortionDryWetNode = ctx.createGain();
    distortionDryWetNode.gain.value = 0; // Bypassed

    // STEMS Audio Isolation Processing Graph (VirtualDJ 3-Stem Architecture)
    // Beats Stem (Kick, Sub-Bass & Low-end Punch)
    const stemBeatsLowPass = ctx.createBiquadFilter();
    stemBeatsLowPass.type = "lowpass";
    stemBeatsLowPass.frequency.value = 260;
    stemBeatsLowPass.Q.value = 0.707;

    const stemBeatsPunch = ctx.createBiquadFilter();
    stemBeatsPunch.type = "peaking";
    stemBeatsPunch.frequency.value = 80;
    stemBeatsPunch.Q.value = 1.0;
    stemBeatsPunch.gain.value = 2;

    const stemBeatsGain = ctx.createGain();
    stemBeatsGain.gain.value = 1.0;

    stemBeatsLowPass.connect(stemBeatsPunch);
    stemBeatsPunch.connect(stemBeatsGain);

    // Vocal Stem (Speech / Vocal Formants / Mid-Range Center)
    const stemVocalHighPass = ctx.createBiquadFilter();
    stemVocalHighPass.type = "highpass";
    stemVocalHighPass.frequency.value = 250;
    stemVocalHighPass.Q.value = 0.707;

    const stemVocalLowPass = ctx.createBiquadFilter();
    stemVocalLowPass.type = "lowpass";
    stemVocalLowPass.frequency.value = 3800;
    stemVocalLowPass.Q.value = 0.707;

    const stemVocalFormant = ctx.createBiquadFilter();
    stemVocalFormant.type = "peaking";
    stemVocalFormant.frequency.value = 1800;
    stemVocalFormant.Q.value = 1.2;
    stemVocalFormant.gain.value = 3;

    const stemVocalGain = ctx.createGain();
    stemVocalGain.gain.value = 1.0;

    stemVocalHighPass.connect(stemVocalLowPass);
    stemVocalLowPass.connect(stemVocalFormant);
    stemVocalFormant.connect(stemVocalGain);

    // Instrumental Stem (Melodic / Guitars / Synths / High Cymbals)
    const stemInstHighPass = ctx.createBiquadFilter();
    stemInstHighPass.type = "highpass";
    stemInstHighPass.frequency.value = 250;
    stemInstHighPass.Q.value = 0.707;

    const stemInstNotch = ctx.createBiquadFilter();
    stemInstNotch.type = "notch";
    stemInstNotch.frequency.value = 1800;
    stemInstNotch.Q.value = 1.0;

    const stemInstHighShelf = ctx.createBiquadFilter();
    stemInstHighShelf.type = "highshelf";
    stemInstHighShelf.frequency.value = 4000;
    stemInstHighShelf.gain.value = 2;

    const stemInstGain = ctx.createGain();
    stemInstGain.gain.value = 1.0;

    stemInstHighPass.connect(stemInstNotch);
    stemInstNotch.connect(stemInstHighShelf);
    stemInstHighShelf.connect(stemInstGain);

    // Stems Summing Node
    const stemsSumNode = ctx.createGain();
    stemsSumNode.gain.value = 1.0;

    stemBeatsGain.connect(stemsSumNode);
    stemVocalGain.connect(stemsSumNode);
    stemInstGain.connect(stemsSumNode);

    // Assemble FX Sub-Graph:
    // Input -> EQ Low -> EQ Mid -> EQ High -> Stems Splitter -> Stems Sum -> Filter -> Gain -> Panner -> Output Split
    eqLowNode.connect(eqMidNode);
    eqMidNode.connect(eqHighNode);

    // Split EQ output into 3 stem filters
    eqHighNode.connect(stemBeatsLowPass);
    eqHighNode.connect(stemVocalHighPass);
    eqHighNode.connect(stemInstHighPass);

    // Connect Stems Summing Node to Main Channel Filter
    stemsSumNode.connect(filterNode);
    filterNode.connect(deckGainNode);
    deckGainNode.connect(pannerNode);

    // From Panner to FX:
    const fxOutputNode = ctx.createGain();
    fxOutputNode.gain.value = 1.0;

    // Dry path
    pannerNode.connect(fxOutputNode);

    // Delay path (parallel)
    pannerNode.connect(delayNode);
    delayNode.connect(delayFeedbackNode);
    delayFeedbackNode.connect(delayNode); // feedback loop
    delayNode.connect(delayDryWetNode);
    delayDryWetNode.connect(fxOutputNode);

    // Reverb path (parallel)
    pannerNode.connect(reverbNode);
    reverbNode.connect(reverbDryWetNode);
    reverbDryWetNode.connect(fxOutputNode);

    // Distortion path (serial replacement or parallel overlay)
    pannerNode.connect(distortionNode);
    distortionNode.connect(distortionDryWetNode);
    distortionDryWetNode.connect(fxOutputNode);

    // Connect final FX output to Analyser
    fxOutputNode.connect(analyserNode);

    // Connect Analyser to appropriate crossfader side
    this.routeDeckToCrossfader(analyserNode, crossfadeSide);

    // Deck state tracking
    const deck = {
      id,
      crossfadeSide,
      audioBuffer: null,
      sourceNode: null,
      nodes: {
        analyser: analyserNode,
        eqLow: eqLowNode,
        eqMid: eqMidNode,
        eqHigh: eqHighNode,
        stemBeatsGain,
        stemVocalGain,
        stemInstGain,
        stemsSum: stemsSumNode,
        filter: filterNode,
        gain: deckGainNode,
        panner: pannerNode,
        delay: delayNode,
        delayFeedback: delayFeedbackNode,
        delayDryWet: delayDryWetNode,
        reverb: reverbNode,
        reverbDryWet: reverbDryWetNode,
        distortion: distortionNode,
        distortionDryWet: distortionDryWetNode,
        fxOutput: fxOutputNode
      },
      stems: {
        vocal: 0,        // -100 to +100 (0 = 100% original)
        instrumental: 0, // -100 to +100
        beats: 0,        // -100 to +100
        mode: "stems"    // "stems" | "eq"
      },
      fxConfig: {
        filterType: "lowpass", // lowpass | highpass
        filterCutoff: 20000,
        delayTime: 0.3,
        delayFeedback: 0.4,
        delayMix: 0,
        reverbMix: 0,
        distortionAmount: 0
      },
      isPlaying: false,
      startTime: 0,
      startOffset: 0,
      duration: 0,
      playbackRate: 1.0,
      pitchPercent: 0, // -10 to +10%
      loop: false,
      loopStart: 0,
      loopEnd: 0,
      cues: [], // list of cue time stamps
      bpm: 120,
      originalBpm: 120,
      name: "No track loaded",
      thumbnail: null,
      channel: null,
      volume: 0.8,
      pan: 0,
      isMuted: false,
      isSoloed: false,
      reverseBuffer: null // Used for backing scratch/reverse play
    };

    this.decks.set(id, deck);
    return deck;
  }

  /**
   * Route deck output analyser node to crossfader channels
   */
  routeDeckToCrossfader(analyserNode, crossfadeSide) {
    if (!analyserNode) return;
    try {
      analyserNode.disconnect();
    } catch (e) {}

    if (crossfadeSide === "left") {
      analyserNode.connect(this.crossfadeLeftGain);
    } else if (crossfadeSide === "right") {
      analyserNode.connect(this.crossfadeRightGain);
    } else {
      analyserNode.connect(this.masterCompressorNode); // Center (direct)
    }
  }

  /**
   * Change crossfader routing of a deck
   */
  setDeckCrossfadeSide(id, side) {
    const deck = this.decks.get(id);
    if (!deck) return;
    deck.crossfadeSide = side;
    this.routeDeckToCrossfader(deck.nodes.analyser, side);
  }

  /**
   * Load audio file/blob into deck
   */
  async loadTrack(id, fileOrBlob, metadata = {}) {
    await this.ensureContextActive();
    const deck = this.decks.get(id);
    if (!deck) return;

    this.stopDeck(id);

    // Read file as ArrayBuffer
    const arrayBuffer = await fileOrBlob.arrayBuffer();
    
    // Decode audio data
    // Deep clone array buffer since decodeAudioData consumes it
    const decodedBuffer = await this.audioContext.decodeAudioData(arrayBuffer.slice(0));
    
    deck.audioBuffer = decodedBuffer;
    deck.duration = decodedBuffer.duration;
    deck.name = fileOrBlob.name || metadata.title || "Uploaded Track";
    deck.thumbnail = metadata.thumbnail || null;
    deck.channel = metadata.channel || null;
    deck.startOffset = 0;
    deck.cues = [0]; // default cue at start

    // Create reverse buffer for scratching / reversing
    deck.reverseBuffer = this.createReverseAudioBuffer(decodedBuffer);

    // Auto-detect BPM
    try {
      const detected = await detectBPM(decodedBuffer);
      deck.originalBpm = detected;
      deck.bpm = detected;
    } catch (e) {
      console.warn("BPM detection failed, default 128 used", e);
      deck.originalBpm = 128;
      deck.bpm = 128;
    }

    // Set loop range to whole song by default
    deck.loopStart = 0;
    deck.loopEnd = decodedBuffer.duration;

    return deck;
  }

  /**
   * Load track from direct URL
   */
  async loadTrackFromUrl(id, url, metadata = {}) {
    await this.ensureContextActive();
    const deck = this.decks.get(id);
    if (!deck) return;

    deck.name = "Loading track...";
    
    const response = await fetch(url);
    const blob = await response.blob();
    
    return this.loadTrack(id, blob, {
      title: metadata.title || url.substring(url.lastIndexOf("/") + 1),
      ...metadata
    });
  }

  /**
   * Helper to reverse audio channels for scratch / backspin simulation
   */
  createReverseAudioBuffer(buffer) {
    const ctx = this.audioContext;
    const numChannels = buffer.numberOfChannels;
    const length = buffer.length;
    const sampleRate = buffer.sampleRate;
    
    const reverse = ctx.createBuffer(numChannels, length, sampleRate);
    
    for (let c = 0; c < numChannels; c++) {
      const sourceData = buffer.getChannelData(c);
      const reverseData = reverse.getChannelData(c);
      for (let i = 0; i < length; i++) {
        reverseData[i] = sourceData[length - 1 - i];
      }
    }
    return reverse;
  }

  /**
   * Play deck
   */
  playDeck(id) {
    const deck = this.decks.get(id);
    if (!deck || !deck.audioBuffer || deck.isPlaying) return;

    const ctx = this.audioContext;
    
    // Create new buffer source
    const sourceNode = ctx.createBufferSource();
    sourceNode.buffer = deck.audioBuffer;
    
    // Apply speed (pitch) fader
    sourceNode.playbackRate.value = deck.playbackRate;

    // Apply loop status
    sourceNode.loop = deck.loop;
    sourceNode.loopStart = deck.loopStart;
    sourceNode.loopEnd = deck.loopEnd;

    // Connect to deck input (eqLow)
    sourceNode.connect(deck.nodes.eqLow);

    // Calculate position
    const offset = deck.startOffset;
    
    // Start playback
    sourceNode.start(0, offset);
    
    deck.sourceNode = sourceNode;
    deck.startTime = ctx.currentTime;
    deck.isPlaying = true;

    // Handle automated stop on track finish (unless looping)
    sourceNode.onended = () => {
      // If we are looping, this won't trigger or we ignore it
      // Standard ended callback:
      if (deck.isPlaying && !sourceNode.loop) {
        deck.isPlaying = false;
        deck.startOffset = 0;
        deck.sourceNode = null;
        if (this.onDeckEnded) {
          this.onDeckEnded(id);
        }
      }
    };
  }

  /**
   * Pause deck
   */
  pauseDeck(id) {
    const deck = this.decks.get(id);
    if (!deck || !deck.isPlaying || !deck.sourceNode) return;

    const elapsed = this.audioContext.currentTime - deck.startTime;
    deck.startOffset = deck.startOffset + elapsed * deck.playbackRate;
    if (deck.startOffset > deck.duration) {
      deck.startOffset = 0;
    }

    try {
      deck.sourceNode.onended = null;
      deck.sourceNode.stop();
    } catch (e) {}

    deck.sourceNode = null;
    deck.isPlaying = false;
  }

  /**
   * Seek deck to time in seconds
   */
  seekDeck(id, timeSeconds) {
    const deck = this.decks.get(id);
    if (!deck || !deck.audioBuffer) return;

    const clampedTime = Math.max(0, Math.min(deck.duration, timeSeconds));
    const wasPlaying = deck.isPlaying;

    if (wasPlaying) {
      this.pauseDeck(id);
      deck.startOffset = clampedTime;
      this.playDeck(id);
    } else {
      deck.startOffset = clampedTime;
    }
  }

  /**
   * Trigger Cue Point
   */
  triggerCue(id) {
    const deck = this.decks.get(id);
    if (!deck || !deck.audioBuffer) return;

    const targetCue = deck.cues.length > 0 ? deck.cues[0] : 0;
    this.seekDeck(id, targetCue);
    
    // If not playing, trigger play temporarily (standard CDJ cue style: play while held or tap play)
    if (!deck.isPlaying) {
      this.playDeck(id);
    }
  }

  /**
   * Set / Save Cue Point
   */
  setCuePoint(id, index = 0) {
    const deck = this.decks.get(id);
    if (!deck) return;
    
    const currentTime = this.getDeckCurrentTime(id);
    deck.cues[index] = currentTime;
  }

  /**
   * Clear Cue Point
   */
  clearCuePoint(id, index = 0) {
    const deck = this.decks.get(id);
    if (!deck) return;
    deck.cues = deck.cues.filter((_, idx) => idx !== index);
  }

  /**
   * Stop deck completely
   */
  stopDeck(id) {
    const deck = this.decks.get(id);
    if (!deck) return;

    if (deck.sourceNode) {
      try {
        deck.sourceNode.onended = null;
        deck.sourceNode.stop();
      } catch (e) {}
      deck.sourceNode = null;
    }
    deck.isPlaying = false;
    deck.startOffset = 0;
  }

  /**
   * Get Current Time of a deck in seconds
   */
  getDeckCurrentTime(id) {
    const deck = this.decks.get(id);
    if (!deck) return 0;
    if (!deck.isPlaying) return deck.startOffset;

    const elapsed = this.audioContext.currentTime - deck.startTime;
    let curr = deck.startOffset + elapsed * deck.playbackRate;
    
    if (deck.loop) {
      // Loop wrapping simulation for visual playhead
      const loopLen = deck.loopEnd - deck.loopStart;
      if (curr > deck.loopEnd) {
        curr = deck.loopStart + ((curr - deck.loopStart) % loopLen);
      }
    } else if (curr > deck.duration) {
      curr = deck.duration;
    }
    return curr;
  }

  /**
   * Set Pitch percentage (-10 to +10%)
   */
  setDeckPitch(id, pitchPercent) {
    const deck = this.decks.get(id);
    if (!deck) return;

    deck.pitchPercent = pitchPercent;
    // playbackRate mapping: e.g. +10% pitch slider = 1.1x speed. -10% = 0.9x speed
    deck.playbackRate = 1.0 + pitchPercent / 100;
    deck.bpm = Math.round(deck.originalBpm * deck.playbackRate);

    // Apply pitch speed immediately if playing
    if (deck.sourceNode && this.audioContext) {
      deck.sourceNode.playbackRate.setTargetAtTime(
        deck.playbackRate,
        this.audioContext.currentTime,
        0.02
      );
    }
  }

  /**
   * Temp speed nudge (pitch bend for beat matching)
   */
  nudgeDeckPitch(id, direction, amount = 0.05) {
    const deck = this.decks.get(id);
    if (!deck) return;

    // Apply temporary speed modifier (e.g. +5% or -5%)
    const nudgedRate = deck.playbackRate + (direction > 0 ? amount : -amount);
    
    if (deck.sourceNode && this.audioContext) {
      deck.sourceNode.playbackRate.setValueAtTime(nudgedRate, this.audioContext.currentTime);
      
      // Ramp back to fader playbackRate after 300ms
      setTimeout(() => {
        if (deck.sourceNode && this.audioContext) {
          deck.sourceNode.playbackRate.setTargetAtTime(
            deck.playbackRate,
            this.audioContext.currentTime,
            0.15
          );
        }
      }, 300);
    }
  }

  /**
   * Match BPM of one deck to another (Sync)
   */
  syncDeckBpm(id, targetBpm) {
    const deck = this.decks.get(id);
    if (!deck || !deck.originalBpm) return;

    // Calculate pitch percentage required to reach target BPM
    // originalBpm * playbackRate = targetBpm
    // playbackRate = targetBpm / originalBpm
    // pitchPercent = (playbackRate - 1.0) * 100
    const neededRate = targetBpm / deck.originalBpm;
    const pitchPct = (neededRate - 1.0) * 100;
    
    // Clamp pitch between -16% and +16%
    const clampedPct = Math.max(-16, Math.min(16, pitchPct));
    this.setDeckPitch(id, clampedPct);
  }

  /**
   * Set Loop status
   */
  setDeckLoop(id, loopActive, start = 0, end = null) {
    const deck = this.decks.get(id);
    if (!deck) return;

    deck.loop = loopActive;
    deck.loopStart = start;
    deck.loopEnd = end || deck.duration;

    if (deck.sourceNode) {
      deck.sourceNode.loop = loopActive;
      deck.sourceNode.loopStart = deck.loopStart;
      deck.sourceNode.loopEnd = deck.loopEnd;
    }
  }

  /**
   * Channel volume fader
   */
  setDeckVolume(id, volume) {
    const deck = this.decks.get(id);
    if (!deck) return;

    deck.volume = volume;
    if (deck.nodes.gain && this.audioContext) {
      deck.nodes.gain.gain.setTargetAtTime(
        deck.isMuted ? 0 : volume,
        this.audioContext.currentTime,
        0.01
      );
    }
  }

  /**
   * Channel Pan
   */
  setDeckPan(id, pan) {
    const deck = this.decks.get(id);
    if (!deck) return;

    deck.pan = pan;
    if (deck.nodes.panner && this.audioContext) {
      deck.nodes.panner.pan.setTargetAtTime(pan, this.audioContext.currentTime, 0.02);
    }
  }

  /**
   * EQ: Low (-12dB to +12dB)
   */
  setDeckEqLow(id, val) {
    const deck = this.decks.get(id);
    if (!deck) return;
    if (deck.nodes.eqLow && this.audioContext) {
      deck.nodes.eqLow.gain.setTargetAtTime(val, this.audioContext.currentTime, 0.01);
    }
  }

  /**
   * EQ: Mid (-12dB to +12dB)
   */
  setDeckEqMid(id, val) {
    const deck = this.decks.get(id);
    if (!deck) return;
    if (deck.nodes.eqMid && this.audioContext) {
      deck.nodes.eqMid.gain.setTargetAtTime(val, this.audioContext.currentTime, 0.01);
    }
  }

  /**
   * EQ: High (-12dB to +12dB)
   */
  setDeckEqHigh(id, val) {
    const deck = this.decks.get(id);
    if (!deck) return;
    if (deck.nodes.eqHigh && this.audioContext) {
      deck.nodes.eqHigh.gain.setTargetAtTime(val, this.audioContext.currentTime, 0.01);
    }
  }

  /**
   * Lowpass / Highpass Filter control
   * val goes from -100 (LPF maximum cut) to +100 (HPF maximum cut)
   * 0 = bypass
   */
  setDeckFilter(id, val) {
    const deck = this.decks.get(id);
    if (!deck) return;

    const ctx = this.audioContext;
    const filter = deck.nodes.filter;
    if (!filter || !ctx) return;

    if (val < 0) {
      // Lowpass filter
      filter.type = "lowpass";
      // Logarithmic sweep from 200Hz to 20000Hz
      const percent = (val + 100) / 100; // 0 to 1
      const freq = 200 + percent * percent * 19800;
      filter.frequency.setTargetAtTime(freq, ctx.currentTime, 0.01);
      deck.fxConfig.filterType = "lowpass";
      deck.fxConfig.filterCutoff = freq;
    } else if (val > 0) {
      // Highpass filter
      filter.type = "highpass";
      // Logarithmic sweep from 20Hz to 4000Hz
      const percent = val / 100; // 0 to 1
      const freq = 20 + percent * percent * 3980;
      filter.frequency.setTargetAtTime(freq, ctx.currentTime, 0.01);
      deck.fxConfig.filterType = "highpass";
      deck.fxConfig.filterCutoff = freq;
    } else {
      // Bypass
      filter.type = "lowpass";
      filter.frequency.setTargetAtTime(20000, ctx.currentTime, 0.01);
      deck.fxConfig.filterType = "lowpass";
      deck.fxConfig.filterCutoff = 20000;
    }
  }

  /**
   * Calculate and apply 3-Stem gain levels based on Left/Center/Right values (-100 to +100)
   * Left (-100 to 0): Attenuate this stem (0% at -100)
   * Center (0): 100% original song with no effect
   * Right (0 to +100): Solo this stem (attenuates the other two stems)
   */
  updateDeckStemGains(id) {
    const deck = this.decks.get(id);
    if (!deck || !this.audioContext) return;

    if (!deck.stems) {
      deck.stems = { vocal: 0, instrumental: 0, beats: 0, mode: "stems" };
    }

    const { vocal, instrumental, beats } = deck.stems;
    const v = vocal / 100;
    const i = instrumental / 100;
    const b = beats / 100;

    // Direct individual attenuation when turned Left (< 0)
    const directVocal = v < 0 ? Math.max(0, 1 + v) : 1.0;
    const directInst  = i < 0 ? Math.max(0, 1 + i) : 1.0;
    const directBeats = b < 0 ? Math.max(0, 1 + b) : 1.0;

    // Solo suppression factor when other knobs are turned Right (> 0)
    const vocalSoloSuppression = Math.max(0, 1 - Math.max(0, i)) * Math.max(0, 1 - Math.max(0, b));
    const instSoloSuppression  = Math.max(0, 1 - Math.max(0, v)) * Math.max(0, 1 - Math.max(0, b));
    const beatsSoloSuppression = Math.max(0, 1 - Math.max(0, v)) * Math.max(0, 1 - Math.max(0, i));

    // Final gain factor for each stem
    const gainVocal = directVocal * vocalSoloSuppression;
    const gainInst  = directInst  * instSoloSuppression;
    const gainBeats = directBeats * beatsSoloSuppression;

    const now = this.audioContext.currentTime;
    if (deck.nodes.stemVocalGain) {
      deck.nodes.stemVocalGain.gain.setTargetAtTime(gainVocal, now, 0.01);
    }
    if (deck.nodes.stemInstGain) {
      deck.nodes.stemInstGain.gain.setTargetAtTime(gainInst, now, 0.01);
    }
    if (deck.nodes.stemBeatsGain) {
      deck.nodes.stemBeatsGain.gain.setTargetAtTime(gainBeats, now, 0.01);
    }
  }

  /**
   * Set Vocal Stem (-100 to +100)
   */
  setDeckStemVocal(id, val) {
    const deck = this.decks.get(id);
    if (!deck) return;
    if (!deck.stems) deck.stems = { vocal: 0, instrumental: 0, beats: 0, mode: "stems" };
    deck.stems.vocal = Math.max(-100, Math.min(100, val));
    this.updateDeckStemGains(id);
  }

  /**
   * Set Instrumental Stem (-100 to +100)
   */
  setDeckStemInstrumental(id, val) {
    const deck = this.decks.get(id);
    if (!deck) return;
    if (!deck.stems) deck.stems = { vocal: 0, instrumental: 0, beats: 0, mode: "stems" };
    deck.stems.instrumental = Math.max(-100, Math.min(100, val));
    this.updateDeckStemGains(id);
  }

  /**
   * Set Beats Stem (-100 to +100)
   */
  setDeckStemBeats(id, val) {
    const deck = this.decks.get(id);
    if (!deck) return;
    if (!deck.stems) deck.stems = { vocal: 0, instrumental: 0, beats: 0, mode: "stems" };
    deck.stems.beats = Math.max(-100, Math.min(100, val));
    this.updateDeckStemGains(id);
  }

  /**
   * Set Mixer Knob Mode: "stems" | "eq"
   */
  setDeckStemMode(id, mode) {
    const deck = this.decks.get(id);
    if (!deck) return;
    if (!deck.stems) deck.stems = { vocal: 0, instrumental: 0, beats: 0, mode: "stems" };
    deck.stems.mode = mode;
  }

  /**
   * Set Deck FX parameters
   */
  setDeckDelay(id, mix, time = 0.3, feedback = 0.4) {
    const deck = this.decks.get(id);
    if (!deck) return;

    const ctx = this.audioContext;
    deck.fxConfig.delayMix = mix;
    deck.fxConfig.delayTime = time;
    deck.fxConfig.delayFeedback = feedback;

    if (ctx) {
      deck.nodes.delay.delayTime.setTargetAtTime(time, ctx.currentTime, 0.02);
      deck.nodes.delayFeedback.gain.setTargetAtTime(feedback, ctx.currentTime, 0.01);
      
      // Constant power mix
      const wetGain = mix;
      deck.nodes.delayDryWet.gain.setTargetAtTime(wetGain, ctx.currentTime, 0.01);
    }
  }

  setDeckReverb(id, mix) {
    const deck = this.decks.get(id);
    if (!deck) return;

    deck.fxConfig.reverbMix = mix;
    if (this.audioContext) {
      deck.nodes.reverbDryWet.gain.setTargetAtTime(mix, this.audioContext.currentTime, 0.01);
    }
  }

  setDeckDistortion(id, amount) {
    const deck = this.decks.get(id);
    if (!deck) return;

    deck.fxConfig.distortionAmount = amount;
    const ctx = this.audioContext;
    if (!ctx) return;

    const shaper = deck.nodes.distortion;
    if (amount > 0) {
      // Generate distortion curve
      const k = amount * 10;
      const n_samples = 44100;
      const curve = new Float32Array(n_samples);
      const deg = Math.PI / 180;
      for (let i = 0; i < n_samples; ++i) {
        const x = (i * 2) / n_samples - 1;
        curve[i] = ((3 + k) * x * 20 * deg) / (Math.PI + k * Math.abs(x));
      }
      shaper.curve = curve;
      deck.nodes.distortionDryWet.gain.setTargetAtTime(amount, ctx.currentTime, 0.01);
    } else {
      shaper.curve = null;
      deck.nodes.distortionDryWet.gain.setTargetAtTime(0, ctx.currentTime, 0.01);
    }
  }

  /**
   * Set Solo status for a deck
   */
  setDeckSolo(id, soloActive) {
    const targetDeck = this.decks.get(id);
    if (!targetDeck) return;

    targetDeck.isSoloed = soloActive;

    // Check if any deck is soloed
    let anySoloed = false;
    this.decks.forEach(d => {
      if (d.isSoloed) anySoloed = true;
    });

    // Update gain nodes
    this.decks.forEach(d => {
      const activeVol = d.volume;
      let targetGain = activeVol;

      if (anySoloed) {
        targetGain = d.isSoloed ? activeVol : 0;
      } else {
        targetGain = d.isMuted ? 0 : activeVol;
      }

      if (d.nodes.gain && this.audioContext) {
        d.nodes.gain.gain.setTargetAtTime(targetGain, this.audioContext.currentTime, 0.01);
      }
    });
  }

  /**
   * Set Mute status for a deck
   */
  setDeckMute(id, muteActive) {
    const deck = this.decks.get(id);
    if (!deck) return;

    deck.isMuted = muteActive;
    
    // Recalculate gains based on Solo rules
    let anySoloed = false;
    this.decks.forEach(d => {
      if (d.isSoloed) anySoloed = true;
    });

    const activeVol = deck.volume;
    let targetGain = activeVol;

    if (anySoloed) {
      targetGain = deck.isSoloed ? activeVol : 0;
    } else {
      targetGain = muteActive ? 0 : activeVol;
    }

    if (deck.nodes.gain && this.audioContext) {
      deck.nodes.gain.gain.setTargetAtTime(targetGain, this.audioContext.currentTime, 0.01);
    }
  }

  /**
   * Destroy a single deck
   */
  destroyDeck(id) {
    const deck = this.decks.get(id);
    if (!deck) return;

    this.stopDeck(id);

    // Disconnect nodes
    Object.values(deck.nodes).forEach(node => {
      if (node) {
        try {
          node.disconnect();
        } catch (e) {}
      }
    });

    this.decks.delete(id);
  }

  /**
   * Platter scratch action (simulates needle scrub)
   */
  scratchDeck(id, speedFactor) {
    const deck = this.decks.get(id);
    if (!deck || !deck.audioBuffer) return;

    const ctx = this.audioContext;
    if (!ctx) return;

    // Fast scrubbing updates offset
    const direction = speedFactor >= 0 ? 1 : -1;
    const absSpeed = Math.abs(speedFactor);

    if (absSpeed > 0.01) {
      // Modify offset based on speed
      const step = direction * absSpeed * 0.05; // scaling factor
      deck.startOffset = Math.max(0, Math.min(deck.duration, deck.startOffset + step));

      // Playing scratching noise using oscillating filter
      if (deck.isPlaying && deck.sourceNode) {
        // Manipulate playbackRate dynamically based on scratch speed
        const scratchPlaybackRate = Math.max(0.1, Math.min(3.0, absSpeed * 2)) * direction;
        
        // If scratching backwards, Web Audio API doesn't support negative playbackRate on standard buffers.
        // So we swap sourceNode buffer to the pre-rendered reverse buffer!
        const reverseBufferRequired = scratchPlaybackRate < 0;
        const currentBuffer = reverseBufferRequired ? deck.reverseBuffer : deck.audioBuffer;
        
        if (currentBuffer && deck.sourceNode.buffer !== currentBuffer) {
          // Re-create node pointing to correct direction
          try {
            deck.sourceNode.stop();
          } catch(e){}
          
          const sourceNode = ctx.createBufferSource();
          sourceNode.buffer = currentBuffer;
          sourceNode.connect(deck.nodes.eqLow);
          
          // If backwards, our offset needs to flip:
          const playOffset = reverseBufferRequired ? (deck.duration - deck.startOffset) : deck.startOffset;
          sourceNode.start(0, Math.max(0, Math.min(deck.duration, playOffset)));
          
          deck.sourceNode = sourceNode;
          deck.startTime = ctx.currentTime;
        }

        // Apply pitch velocity
        deck.sourceNode.playbackRate.setValueAtTime(
          Math.abs(scratchPlaybackRate),
          ctx.currentTime
        );
      }
    }
  }

  /**
   * Release platter scratch, return to normal pitch fader rate
   */
  releaseScratch(id) {
    const deck = this.decks.get(id);
    if (!deck || !deck.audioBuffer) return;
    
    if (deck.isPlaying) {
      // Restore forward play
      this.pauseDeck(id);
      this.playDeck(id);
    }
  }

  /**
   * Set entire audio output sink target (Bluetooth speakers, headphones, etc.)
   */
  async setAudioOutputDevice(sinkId) {
    if (!this.audioContext) return false;
    try {
      if (typeof this.audioContext.setSinkId === "function") {
        await this.audioContext.setSinkId(sinkId);
        return true;
      } else {
        console.warn("AudioContext.setSinkId is not supported in this browser.");
        return false;
      }
    } catch (e) {
      console.error("Failed to set audio output device:", e);
      return false;
    }
  }

  // ==========================================
  // MICROPHONE CONTROLS
  // ==========================================

  /**
   * Toggle / Start microphone input
   */
  async startMicrophone(deviceId = null, inputMode = null) {
    await this.ensureContextActive();
    const ctx = this.audioContext;

    if (this.micStream) {
      this.stopMicrophone(true); // restart: keep recovery listeners and backoff counter
    }

    if (inputMode) this.micInputMode = inputMode;
    this.micActiveDeviceFellBack = false;
    const speakerSafe = this.micInputMode === "speaker";

    try {
      // Browser voice DSP is OFF in raw mode on purpose. With it on, Chrome's echo
      // canceller uses the system output as its reference — and since we monitor the
      // live mic through that same output, it subtracts your own voice as "echo",
      // which swallows syllables and leaves gurgling residue. Only speaker-safe mode
      // pays that price, because there it is the thing preventing acoustic howl.
      const constraints = {
        audio: {
          echoCancellation: speakerSafe,
          noiseSuppression: speakerSafe,
          autoGainControl: false,   // always off: AGC pumps and ducks mid-word
          channelCount: 1
        },
        video: false
      };

      if (deviceId) {
        constraints.audio.deviceId = { exact: deviceId };
      }

      // macOS invalidates deviceIds when a headset is plugged or unplugged, and an
      // { exact } deviceId that no longer exists throws OverconstrainedError. Fall back
      // to the system default rather than leaving the console silently dead.
      try {
        this.micStream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err) {
        const recoverable = ["OverconstrainedError", "NotFoundError", "NotReadableError"];
        if (deviceId && recoverable.includes(err.name)) {
          console.warn(`Mic device ${deviceId} unavailable (${err.name}) — falling back to system default`);
          delete constraints.audio.deviceId;
          this.micStream = await navigator.mediaDevices.getUserMedia(constraints);
          this.micActiveDeviceFellBack = true;
        } else {
          throw err;
        }
      }

      this.micSourceNode = ctx.createMediaStreamSource(this.micStream);
      this.micRequestedDeviceId = deviceId;
      this.watchMicTrackHealth();

      this.micAnalyserNode = ctx.createAnalyser();
      this.micAnalyserNode.fftSize = 2048; // enough resolution for the feedback guard's spectral peak test

      this.micGainNode = ctx.createGain();
      this.micGainNode.gain.value = 1.0; // Clean 100% vocal recording level

      // Dedicated smooth vocal dynamics compressor (prevents harsh clipping & word truncation)
      this.micCompressorNode = ctx.createDynamicsCompressor();
      this.micCompressorNode.threshold.value = -12;
      this.micCompressorNode.knee.value = 20;
      this.micCompressorNode.ratio.value = 2.5;
      this.micCompressorNode.attack.value = 0.015;
      this.micCompressorNode.release.value = 0.25;

      // Sub-rumble highpass filter at 50Hz (preserves full warm voice fundamentals down to 50Hz)
      this.micHighpassNode = ctx.createBiquadFilter();
      this.micHighpassNode.type = "highpass";
      this.micHighpassNode.frequency.value = 50;

      // Anti-Feedback Notch at 3150Hz. Only engaged in speaker-safe mode: 3150Hz with
      // Q=3 sits in the consonant/presence band, so leaving it in permanently blurs
      // s/t/k/sh and makes clean speech sound like it is dropping out.
      this.micFeedbackNotchNode = ctx.createBiquadFilter();
      this.micFeedbackNotchNode.type = speakerSafe ? "notch" : "allpass";
      this.micFeedbackNotchNode.frequency.value = 3150;
      this.micFeedbackNotchNode.Q.value = 3.0;

      // High-Frequency Dampening (stops high-pitched speaker feedback loops).
      // Flat in raw mode so sibilance survives.
      this.micHighDampNode = ctx.createBiquadFilter();
      this.micHighDampNode.type = "highshelf";
      this.micHighDampNode.frequency.value = 6500;
      this.micHighDampNode.gain.value = speakerSafe ? -3 : 0;

      // Runaway-feedback guard on the monitor path (see startMicFeedbackGuard)
      this.micFeedbackGuardNode = ctx.createGain();
      this.micFeedbackGuardNode.gain.value = 1.0;
      this.micFeedbackGuardActive = false;

      // Mic EQ
      this.micEqLow = ctx.createBiquadFilter();
      this.micEqLow.type = "lowshelf";
      this.micEqLow.frequency.value = 150;
      this.micEqLow.gain.value = 0;

      this.micEqMid = ctx.createBiquadFilter();
      this.micEqMid.type = "peaking";
      this.micEqMid.Q.value = 1.0;
      this.micEqMid.frequency.value = 1500;
      this.micEqMid.gain.value = 0;

      this.micEqHigh = ctx.createBiquadFilter();
      this.micEqHigh.type = "highshelf";
      this.micEqHigh.frequency.value = 5000;
      this.micEqHigh.gain.value = 0;

      this.micSourceNode.connect(this.micAnalyserNode);
      this.micAnalyserNode.connect(this.micHighpassNode);
      this.micHighpassNode.connect(this.micFeedbackNotchNode);
      this.micFeedbackNotchNode.connect(this.micHighDampNode);
      this.micHighDampNode.connect(this.micEqLow);
      this.micEqLow.connect(this.micEqMid);
      this.micEqMid.connect(this.micEqHigh);

      // Set mic active status FIRST so applyVoiceFx connects nodes properly on 1st load
      this.isMicActive = true;

      // Connect EQ chain to Voice FX router (defaults to "clean")
      this.applyVoiceFx(this.micVoiceFxType || "clean");

      // Start Auto Ducking monitor loop
      this.startMicDuckingMonitor();

      // Start runaway acoustic feedback protection
      this.startMicFeedbackGuard();

      // Watch for the OS revoking the device or the context stalling
      this.installMicRecoveryListeners();

      this.micStallFrames = 0;
      this.micRecoverAttempts = 0;
      this.setMicHealth("live", this.micActiveDeviceFellBack ? "Using system default input" : null);

      return true;
    } catch (e) {
      console.error("Microphone initialization failed", e);
      this.isMicActive = false;
      const detail = e.name === "NotAllowedError"
        ? "Microphone permission denied"
        : e.name === "NotReadableError"
          ? "Input device is in use by another app"
          : e.name === "NotFoundError"
            ? "No microphone found"
            : e.message || "Could not open input device";
      this.setMicHealth("lost", detail);
      return false;
    }
  }

  /**
   * Report input health to the UI. States: idle | live | recovering | lost
   */
  setMicHealth(health, detail = null) {
    this.micHealth = health;
    this.micHealthDetail = detail;
    if (this.onMicHealthChange) this.onMicHealthChange(health, detail);
  }

  /**
   * A MediaStreamTrack dies quietly. If the OS revokes the device — headset unplugged,
   * another app takes exclusive control, machine wakes from sleep — the track goes
   * "ended" or "muted" while our graph stays wired up and isMicActive stays true, so
   * the console still reads LIVE while capturing nothing. Watch for that and recover.
   */
  watchMicTrackHealth() {
    if (!this.micStream) return;
    const track = this.micStream.getAudioTracks()[0];
    if (!track) return;

    track.onended = () => {
      if (!this.isMicActive) return;
      console.warn("Mic track ended — input device was revoked by the OS");
      this.recoverMicrophone("Input device disconnected");
    };

    track.onmute = () => {
      if (!this.isMicActive) return;
      this.setMicHealth("recovering", "Input muted by the system");
    };

    track.onunmute = () => {
      if (!this.isMicActive) return;
      this.micStallFrames = 0;
      this.setMicHealth("live", null);
    };
  }

  /**
   * React to device topology and audio context changes while the mic is live.
   */
  installMicRecoveryListeners() {
    if (!this.deviceChangeHandler && navigator.mediaDevices) {
      this.deviceChangeHandler = () => {
        if (!this.isMicActive || !this.micStream) return;
        const track = this.micStream.getAudioTracks()[0];
        // Device list changed and our track is no longer live -> reacquire
        if (!track || track.readyState !== "live") {
          this.recoverMicrophone("Input device list changed");
        }
      };
      navigator.mediaDevices.addEventListener("devicechange", this.deviceChangeHandler);
    }

    if (!this.contextStateHandler && this.audioContext) {
      this.contextStateHandler = () => {
        // macOS can suspend or interrupt the context on sleep/device switch. Nothing
        // flows through the graph until it is resumed.
        if (this.audioContext.state !== "running" && this.isMicActive) {
          this.setMicHealth("recovering", "Audio engine suspended — resuming");
          this.audioContext.resume().catch(() => {});
        }
      };
      this.audioContext.addEventListener("statechange", this.contextStateHandler);
    }
  }

  removeMicRecoveryListeners() {
    if (this.deviceChangeHandler && navigator.mediaDevices) {
      navigator.mediaDevices.removeEventListener("devicechange", this.deviceChangeHandler);
      this.deviceChangeHandler = null;
    }
    if (this.contextStateHandler && this.audioContext) {
      this.audioContext.removeEventListener("statechange", this.contextStateHandler);
      this.contextStateHandler = null;
    }
    if (this.micRecoverTimer) {
      clearTimeout(this.micRecoverTimer);
      this.micRecoverTimer = null;
    }
  }

  /**
   * Rebuild the mic chain on the same device if it still exists, otherwise on the
   * system default. Backs off after repeated failures so a missing device does not
   * spin forever.
   */
  async recoverMicrophone(reason = "Input lost") {
    if (this.micRecoverTimer) return; // a recovery pass is already queued

    this.setMicHealth("recovering", reason);
    this.micRecoverAttempts++;

    if (this.micRecoverAttempts > 4) {
      this.stopMicrophone();
      this.setMicHealth("lost", `${reason} — press Enable Vocal Input to retry`);
      return;
    }

    const deviceId = this.micRequestedDeviceId;
    const attempts = this.micRecoverAttempts;
    const delay = Math.min(2000, 250 * attempts);

    this.micRecoverTimer = setTimeout(async () => {
      this.micRecoverTimer = null;
      const ok = await this.startMicrophone(deviceId, this.micInputMode);
      if (ok) {
        this.micRecoverAttempts = 0;
      } else {
        this.recoverMicrophone(reason);
      }
    }, delay);
  }

  /**
   * Stop Microphone
   */
  stopMicrophone(isRestart = false) {
    this.isMicActive = false;

    if (this.micStream) {
      this.micStream.getAudioTracks().forEach(t => {
        t.onended = null;
        t.onmute = null;
        t.onunmute = null;
      });
      this.micStream.getTracks().forEach(t => t.stop());
      this.micStream = null;
    }

    if (!isRestart) {
      this.removeMicRecoveryListeners();
      this.micRecoverAttempts = 0;
      this.setMicHealth("idle", null);
    }

    if (this.micActiveOscillators) {
      this.micActiveOscillators.forEach(osc => {
        try {
          osc.stop();
          osc.disconnect();
        } catch (e) {}
      });
      this.micActiveOscillators = [];
    }

    const nodes = [
      this.micSourceNode,
      this.micAnalyserNode,
      this.micHighpassNode,
      this.micFeedbackNotchNode,
      this.micHighDampNode,
      this.micEqLow,
      this.micEqMid,
      this.micEqHigh,
      this.micGainNode,
      this.micCompressorNode,
      this.micFeedbackGuardNode,
      this.micVoiceFxNode
    ];

    nodes.forEach(n => {
      if (n) {
        try {
          n.disconnect();
        } catch (e) {}
      }
    });

    this.micSourceNode = null;
    this.micAnalyserNode = null;
    this.micHighpassNode = null;
    this.micFeedbackNotchNode = null;
    this.micHighDampNode = null;
    this.micEqLow = null;
    this.micEqMid = null;
    this.micEqHigh = null;
    this.micGainNode = null;
    this.micCompressorNode = null;
    this.micFeedbackGuardNode = null;
    this.micVoiceFxNode = null;
    this.micFeedbackGuardActive = false;
    if (this.onMicFeedbackGuard) this.onMicFeedbackGuard(false);
  }

  /**
   * Set mic volume
   */
  setMicVolume(value) {
    if (!this.micGainNode || !this.audioContext) return;
    // Set mic volume (0 to 1.25 max to prevent speaker feedback overdrive)
    this.micGainNode.gain.setTargetAtTime(value * 1.25, this.audioContext.currentTime, 0.01);
  }

  /**
   * Microphone EQs
   */
  setMicEqLow(val) {
    if (this.micEqLow && this.audioContext) {
      this.micEqLow.gain.setTargetAtTime(val, this.audioContext.currentTime, 0.01);
    }
  }

  setMicEqMid(val) {
    if (this.micEqMid && this.audioContext) {
      this.micEqMid.gain.setTargetAtTime(val, this.audioContext.currentTime, 0.01);
    }
  }

  setMicEqHigh(val) {
    if (this.micEqHigh && this.audioContext) {
      this.micEqHigh.gain.setTargetAtTime(val, this.audioContext.currentTime, 0.01);
    }
  }

  setMicEchoDelayTime(ms) {
    const sec = Math.max(0.1, Math.min(2.0, ms / 1000));
    this.micEchoDelayTime = sec;
    if (this.micEchoDelayNode && this.audioContext) {
      this.micEchoDelayNode.delayTime.setTargetAtTime(sec, this.audioContext.currentTime, 0.02);
    }
  }

  setMicEchoRepeats(count) {
    this.micEchoRepeats = Math.max(1, Math.min(10, count));
    if (this.micEchoFeedbackNode && this.audioContext) {
      const repeats = this.micEchoRepeats;
      const feedbackVal = repeats <= 1 ? 0 : Math.min(0.90, Math.pow(0.08, 1 / repeats));
      this.micEchoFeedbackNode.gain.setTargetAtTime(feedbackVal, this.audioContext.currentTime, 0.02);
    }
  }

  /**
   * Apply voice effects to mic channel
   */
  applyVoiceFx(type) {
    this.micVoiceFxType = type;
    if (!this.isMicActive || !this.micEqHigh || !this.audioContext) return;

    const ctx = this.audioContext;

    // Stop and clear any running voice FX oscillators
    if (this.micActiveOscillators) {
      this.micActiveOscillators.forEach(osc => {
        try {
          osc.stop();
          osc.disconnect();
        } catch (e) {}
      });
      this.micActiveOscillators = [];
    }

    // Disconnect old Voice Fx node
    if (this.micVoiceFxNode) {
      try {
        this.micVoiceFxNode.disconnect();
      } catch (e) {}
      this.micVoiceFxNode = null;
      this.micEchoDelayNode = null;
      this.micEchoFeedbackNode = null;
      this.micEchoFilterNode = null;
    }

    try {
      this.micEqHigh.disconnect();
    } catch (e) {}

    // Create appropriate node chain based on type
    if (type === "clean") {
      this.micEqHigh.connect(this.micGainNode);
    } else if (type === "radio" || type === "telephone") {
      // Bandpass filter
      const filter = ctx.createBiquadFilter();
      filter.type = "bandpass";
      filter.frequency.value = type === "radio" ? 1200 : 1800;
      filter.Q.value = 2.0;

      // Small distortion
      const shaper = ctx.createWaveShaper();
      const makeDistortionCurve = (amount) => {
        const k = amount;
        const n_samples = 44100;
        const curve = new Float32Array(n_samples);
        const deg = Math.PI / 180;
        for (let i = 0; i < n_samples; ++i) {
          const x = (i * 2) / n_samples - 1;
          curve[i] = ((3 + k) * x * 20 * deg) / (Math.PI + k * Math.abs(x));
        }
        return curve;
      };
      shaper.curve = makeDistortionCurve(60);

      this.micEqHigh.connect(filter);
      filter.connect(shaper);
      shaper.connect(this.micGainNode);

      this.micVoiceFxNode = filter; // keep ref
    } else if (type === "robot") {
      // Ring modulation
      const modulator = ctx.createOscillator();
      modulator.type = "sawtooth";
      modulator.frequency.value = 55; // Robot fundamental tone

      const modulationGain = ctx.createGain();
      modulationGain.gain.value = 0.5;

      const ringNode = ctx.createGain();
      ringNode.gain.value = 0; // modulated by oscillator

      // modulator sweeps ring gain
      modulator.connect(modulationGain);
      modulationGain.connect(ringNode.gain);

      this.micEqHigh.connect(ringNode);
      ringNode.connect(this.micGainNode);

      modulator.start();
      this.micActiveOscillators.push(modulator);

      this.micVoiceFxNode = ringNode;
    } else if (type === "megaphone") {
      // Highpass + Peak EQ + Distortion
      const filter = ctx.createBiquadFilter();
      filter.type = "highpass";
      filter.frequency.value = 800;

      const peak = ctx.createBiquadFilter();
      peak.type = "peaking";
      peak.frequency.value = 2000;
      peak.Q.value = 3.0;
      peak.gain.value = 12;

      const distortion = ctx.createWaveShaper();
      const makeCurve = (amount) => {
        const c = new Float32Array(44100);
        for (let i = 0; i < 44100; i++) {
          const x = (i * 2) / 44100 - 1;
          c[i] = x > 0 ? Math.sqrt(x) : -Math.sqrt(Math.abs(x));
        }
        return c;
      };
      distortion.curve = makeCurve(100);

      this.micEqHigh.connect(filter);
      filter.connect(peak);
      peak.connect(distortion);
      distortion.connect(this.micGainNode);

      this.micVoiceFxNode = filter;
    } else if (type === "echo") {
      // Custom DJ Vocal Echo with phrase delay & exact repeat count control
      const delayNode = ctx.createDelay(3.0);
      delayNode.delayTime.value = this.micEchoDelayTime; // e.g. 0.65s (650ms for "Dj Kahnu")

      // Calculate feedback level based on requested repeat count (e.g. 3 repeats)
      const repeats = Math.max(1, Math.min(10, this.micEchoRepeats));
      const feedbackVal = repeats <= 1 ? 0 : Math.min(0.90, Math.pow(0.08, 1 / repeats));

      const feedbackNode = ctx.createGain();
      feedbackNode.gain.value = feedbackVal;

      // Bandpass vocal filter inside feedback loop to keep every phrase repeat clear & crisp without mud
      const loopFilter = ctx.createBiquadFilter();
      loopFilter.type = "bandpass";
      loopFilter.frequency.value = 1800;
      loopFilter.Q.value = 0.6;

      // Loop routing: delay -> filter -> feedback -> delay
      delayNode.connect(loopFilter);
      loopFilter.connect(feedbackNode);
      feedbackNode.connect(delayNode);

      const wetGain = ctx.createGain();
      wetGain.gain.value = 0.55; // strong wet echo level

      // Dry bypass connection (instant clean voice)
      this.micEqHigh.connect(this.micGainNode);

      // Wet delay path connection
      this.micEqHigh.connect(delayNode);
      delayNode.connect(wetGain);
      wetGain.connect(this.micGainNode);

      this.micEchoDelayNode = delayNode;
      this.micEchoFeedbackNode = feedbackNode;
      this.micEchoFilterNode = loopFilter;
      this.micVoiceFxNode = delayNode;
    } else if (type === "reverb") {
      // Big room reverb overlay
      const reverb = ctx.createConvolver();
      reverb.buffer = this.reverbImpulseBuffer;
      const wet = ctx.createGain();
      wet.gain.value = 0.6;

      // Dry bypass
      this.micEqHigh.connect(this.micGainNode);

      // Reverb path
      this.micEqHigh.connect(reverb);
      reverb.connect(wet);
      wet.connect(this.micGainNode);

      this.micVoiceFxNode = reverb;
    } else if (type === "deep" || type === "chipmunk") {
      // Pitch Shifting using delay line LFO modulation
      const delay = ctx.createDelay();
      delay.delayTime.value = 0.015; // 15ms window

      const osc = ctx.createOscillator();
      osc.type = "triangle";
      osc.frequency.value = 4; // 4 Hz LFO frequency

      const oscGain = ctx.createGain();
      oscGain.gain.value = type === "deep" ? -0.008 : 0.008; // -0.008 for deep shift, +0.008 for chipmunk shift

      osc.connect(oscGain);
      oscGain.connect(delay.delayTime);

      this.micEqHigh.connect(delay);
      delay.connect(this.micGainNode);

      osc.start();
      this.micActiveOscillators.push(osc);

      this.micVoiceFxNode = delay;
    }

    // Connect mic output gain to master
    try {
      this.micGainNode.disconnect();
    } catch(e){}
    
    // Connect through the microphone compressor, then the feedback guard, then master
    const monitorTail = this.micFeedbackGuardNode || this.masterCompressorNode;
    if (this.micCompressorNode) {
      this.micGainNode.connect(this.micCompressorNode);
      try { this.micCompressorNode.disconnect(); } catch (e) {}
      this.micCompressorNode.connect(monitorTail);
    } else {
      this.micGainNode.connect(monitorTail);
    }

    if (this.micFeedbackGuardNode) {
      try { this.micFeedbackGuardNode.disconnect(); } catch (e) {}
      this.micFeedbackGuardNode.connect(this.masterCompressorNode);
    }
  }

  /**
   * Set the mic capture mode: "raw" (headphones, best quality) or "speaker"
   * (laptop speakers, browser echo cancellation on). Restarts the stream when live,
   * since echoCancellation/noiseSuppression are capture-time constraints.
   */
  async setMicInputMode(mode, deviceId = null) {
    const next = mode === "speaker" ? "speaker" : "raw";
    if (next === this.micInputMode) return true;
    this.micInputMode = next;
    if (!this.isMicActive) return true;
    return this.startMicrophone(deviceId, next);
  }

  /**
   * Runaway acoustic feedback protection.
   *
   * Raw capture through laptop speakers can build a howl. A howl is not speech: it is
   * loud, it parks on one narrow frequency, and it sustains. Speech constantly moves.
   * So we only duck when a single spectral peak dominates the whole spectrum for
   * several consecutive frames at high level — then recover quickly once it stops.
   */
  startMicFeedbackGuard() {
    if (!this.micAnalyserNode || !this.micFeedbackGuardNode) return;

    const bins = new Uint8Array(this.micAnalyserNode.frequencyBinCount);
    const time = new Uint8Array(this.micAnalyserNode.fftSize);
    let howlFrames = 0;
    let calmFrames = 0;

    const guard = () => {
      if (!this.isMicActive || !this.micAnalyserNode || !this.micFeedbackGuardNode) return;

      this.micAnalyserNode.getByteTimeDomainData(time);
      let sum = 0;
      for (let i = 0; i < time.length; i++) {
        const v = (time[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / time.length);

      this.micAnalyserNode.getByteFrequencyData(bins);
      let peak = 0;
      let total = 0;
      for (let i = 2; i < bins.length; i++) {
        total += bins[i];
        if (bins[i] > peak) peak = bins[i];
      }
      const mean = total / (bins.length - 2);
      // Peak-to-average ratio: a sine-like howl spikes this; broadband speech does not.
      const tonality = mean > 0 ? peak / mean : 0;

      const howling = rms > 0.25 && tonality > 9;

      if (howling) {
        howlFrames++;
        calmFrames = 0;
      } else {
        calmFrames++;
        if (calmFrames > 20) howlFrames = 0;
      }

      // Stall watchdog. A revoked or dead capture device produces a mathematically
      // perfect flat line (every sample exactly 128), which never happens with a real
      // open mic — even a silent room has dither. Sustained flatline, a non-live track,
      // or a non-running context all mean we are wired to nothing.
      const track = this.micStream && this.micStream.getAudioTracks()[0];
      const flatline = rms === 0;
      const deadTrack = !track || track.readyState !== "live";
      const deadContext = this.audioContext.state !== "running";

      if (flatline || deadTrack || deadContext) {
        this.micStallFrames++;
        if (this.micStallFrames === 180) { // ~3s at 60fps
          if (deadContext) {
            this.setMicHealth("recovering", "Audio engine suspended — resuming");
            this.audioContext.resume().catch(() => {});
          } else {
            this.recoverMicrophone(deadTrack ? "Input device disconnected" : "Input went silent");
            return; // recovery restarts this loop
          }
        }
      } else {
        if (this.micStallFrames > 0 && this.micHealth !== "live") {
          this.setMicHealth("live", null);
        }
        this.micStallFrames = 0;
      }

      const ctx = this.audioContext;
      if (howlFrames > 6 && !this.micFeedbackGuardActive) {
        this.micFeedbackGuardActive = true;
        this.micFeedbackGuardNode.gain.setTargetAtTime(0.12, ctx.currentTime, 0.02);
        if (this.onMicFeedbackGuard) this.onMicFeedbackGuard(true);
      } else if (this.micFeedbackGuardActive && calmFrames > 45) {
        this.micFeedbackGuardActive = false;
        this.micFeedbackGuardNode.gain.setTargetAtTime(1.0, ctx.currentTime, 0.15);
        if (this.onMicFeedbackGuard) this.onMicFeedbackGuard(false);
      }

      requestAnimationFrame(guard);
    };

    guard();
  }

  /**
   * Monitor microphone levels and apply auto-ducking to music decks
   */
  startMicDuckingMonitor() {
    if (!this.micAnalyserNode) return;

    const data = new Uint8Array(this.micAnalyserNode.frequencyBinCount);
    
    const monitor = () => {
      if (!this.isMicActive || !this.micAnalyserNode) return;

      this.micAnalyserNode.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);
      
      const speechDetected = rms > 0.04; // threshold

      if (this.autoDucking && speechDetected) {
        // Speech active -> Duck music decks to 30% of fader faders
        this.decks.forEach(deck => {
          if (deck.nodes.gain && this.audioContext) {
            const targetVol = deck.volume * this.duckingFactor;
            deck.nodes.gain.gain.setTargetAtTime(targetVol, this.audioContext.currentTime, 0.08);
          }
        });
      } else {
        // Speech quiet -> Restore original fader volume levels
        this.decks.forEach(deck => {
          if (deck.nodes.gain && this.audioContext) {
            deck.nodes.gain.gain.setTargetAtTime(
              deck.isMuted ? 0 : deck.volume,
              this.audioContext.currentTime,
              0.25 // smooth recovery
            );
          }
        });
      }

      requestAnimationFrame(monitor);
    };

    monitor();
  }

  /**
   * Set music auto-ducking attenuation factor (0.0 to 1.0)
   */
  setDuckingFactor(value) {
    this.duckingFactor = Math.max(0.0, Math.min(1.0, value));
  }

  /**
   * Toggle auto-ducking status
   */
  setAutoDucking(enabled) {
    this.autoDucking = enabled;
  }

  // ==========================================
  // SAMPLER CONTROLS (SYNTHESIZED SOUNDS)
  // ==========================================

  playSample(padIndex) {
    this.initialize();
    const ctx = this.audioContext;
    const time = ctx.currentTime;

    // 16 Real-time Synthesized Sampler Pads
    switch (padIndex) {
      case 0: // Airhorn Burst
        this.synthAirhorn(ctx, time);
        break;
      case 1: // Laser Sweep
        this.synthLaser(ctx, time);
        break;
      case 2: // Sub Bass Drop
        this.synthSubDrop(ctx, time);
        break;
      case 3: // Siren Sweep
        this.synthSiren(ctx, time);
        break;
      case 4: // Impact Boom Drop
        this.synthImpact(ctx, time);
        break;
      case 5: // Riser Uplifter
        this.synthRiser(ctx, time);
        break;
      case 6: // Tape Stop Brake FX
        this.synthTapeStop(ctx, time);
        break;
      case 7: // Vinyl Chirp Scratch
        this.synthChirp(ctx, time);
        break;
      case 8: // Sub Kick Drum
        this.synthKick(ctx, time);
        break;
      case 9: // Snare Drum
        this.synthSnare(ctx, time);
        break;
      case 10: // Hi-Hat Sizzle
        this.synthHihat(ctx, time);
        break;
      case 11: // Reverb Clap
        this.synthClap(ctx, time);
        break;
      case 12: // 808 Cowbell
        this.synthCowbell(ctx, time);
        break;
      case 13: // Beat Roll Fill
        this.synthBeatRoll(ctx, time);
        break;
      case 14: // Trap Rimshot
        this.synthRimshot(ctx, time);
        break;
      case 15: // Sub Kick Roll
        this.synthSubKickRoll(ctx, time);
        break;
      case 16: // "DJ KAHNU" vocal tag
        this.playVocalTag("kahnu", time);
        break;
      case 17: // "DJ SRIKANT" vocal tag
        this.playVocalTag("srikant", time);
        break;
      default:
        break;
    }
  }

  // ==========================================
  // SYNTHESIZED DJ VOCAL TAGS
  // ==========================================

  /**
   * Shared processing bus for a vocal tag: saturation for grit, a presence lift for the
   * PA/megaphone character, and a slapback echo trail. Returns the input node that the
   * phoneme segments feed into.
   */
  createVocalTagBus(ctx, { echo = true } = {}) {
    const input = ctx.createGain();
    input.gain.value = 1.0;

    // Soft saturation — tanh keeps it thick without the harsh fizz of hard clipping
    const shaper = ctx.createWaveShaper();
    const curve = new Float32Array(1024);
    for (let i = 0; i < 1024; i++) {
      const x = (i / 1023) * 2 - 1;
      curve[i] = Math.tanh(x * 2.2);
    }
    shaper.curve = curve;

    const lowCut = ctx.createBiquadFilter();
    lowCut.type = "highpass";
    lowCut.frequency.value = 110;

    // Presence lift where speech intelligibility lives
    const presence = ctx.createBiquadFilter();
    presence.type = "peaking";
    presence.frequency.value = 2200;
    presence.Q.value = 1.2;
    presence.gain.value = 6;

    const out = ctx.createGain();
    out.gain.value = 0.85;

    input.connect(shaper);
    shaper.connect(lowCut);
    lowCut.connect(presence);
    presence.connect(out);
    out.connect(this.samplerGainNode);

    if (echo) {
      const delay = ctx.createDelay(1.0);
      delay.delayTime.value = 0.19;

      const feedback = ctx.createGain();
      feedback.gain.value = 0.30;

      // Darken each repeat so the trail recedes instead of piling up
      const damp = ctx.createBiquadFilter();
      damp.type = "lowpass";
      damp.frequency.value = 2800;

      const wet = ctx.createGain();
      wet.gain.value = 0.32;

      presence.connect(delay);
      delay.connect(damp);
      damp.connect(feedback);
      feedback.connect(delay);
      damp.connect(wet);
      wet.connect(this.samplerGainNode);
    }

    return input;
  }

  /**
   * Lazily decode a vocal tag MP3 into an AudioBuffer and cache it.
   * Decoding once and keeping the PCM means repeat presses are instant.
   */
  async loadVocalTag(key) {
    const tag = VOCAL_TAGS[key];
    if (!tag) return null;
    if (this.vocalTagBuffers[key]) return this.vocalTagBuffers[key];
    if (this.vocalTagLoads[key]) return this.vocalTagLoads[key];

    this.initialize();
    const ctx = this.audioContext;

    this.vocalTagLoads[key] = (async () => {
      try {
        const response = await fetch(tag.url);
        const arrayBuffer = await response.arrayBuffer();
        const decoded = await ctx.decodeAudioData(arrayBuffer);
        this.vocalTagBuffers[key] = decoded;
        return decoded;
      } catch (e) {
        console.warn(`Could not load vocal tag "${key}"`, e);
        return null;
      } finally {
        delete this.vocalTagLoads[key];
      }
    })();

    return this.vocalTagLoads[key];
  }

  /**
   * Warm the vocal tag cache so the first pad press is not delayed by a fetch/decode.
   */
  preloadVocalTags() {
    Object.keys(VOCAL_TAGS).forEach(key => {
      this.loadVocalTag(key).catch(() => {});
    });
  }

  /**
   * Fire a vocal tag through the DJ drop chain.
   *
   * playbackRate below 1 pitches the voice down and lengthens it together, which is
   * exactly how classic DJ tags are made — a true pitch shift that preserved duration
   * would need a phase vocoder and would sound less like a drop, not more.
   */
  playVocalTag(key, time = null) {
    const buffer = this.vocalTagBuffers[key];

    // Not decoded yet: load, then fire as soon as it lands
    if (!buffer) {
      this.loadVocalTag(key).then(loaded => {
        if (loaded) this.playVocalTag(key);
      });
      return;
    }

    const ctx = this.audioContext;
    const at = time !== null ? time : ctx.currentTime;

    const bus = this.createVocalTagBus(ctx);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = 0.87; // ~13% down for weight and authority

    const level = ctx.createGain();
    level.gain.value = 1.0;

    source.connect(level);
    level.connect(bus);

    source.start(at);
  }

  /**
   * Set Master Sampler Pads Volume (0 to 1)
   */
  setSamplerVolume(val) {
    if (!this.samplerGainNode || !this.audioContext) return;
    this.samplerGainNode.gain.setTargetAtTime(val, this.audioContext.currentTime, 0.01);
  }

  /** Impact Sub Boom & Explosion Drop **/
  synthImpact(ctx, time) {
    // Sub sine sweep
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(140, time);
    osc.frequency.exponentialRampToValueAtTime(18, time + 1.2);

    gain.gain.setValueAtTime(0.8, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 1.2);

    osc.connect(gain);
    gain.connect(this.samplerGainNode);
    osc.start(time);
    osc.stop(time + 1.2);

    // Filtered noise rumble tail
    const bufferSize = ctx.sampleRate * 0.8;
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuffer;

    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(400, time);
    filter.frequency.exponentialRampToValueAtTime(30, time + 0.8);

    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.5, time);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, time + 0.8);

    noise.connect(filter);
    filter.connect(noiseGain);
    noiseGain.connect(this.samplerGainNode);
    noise.start(time);
    noise.stop(time + 0.8);
  }

  /** Riser Build-Up Pitch & Noise Filter Uplifter **/
  synthRiser(ctx, time) {
    const duration = 1.2;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(150, time);
    osc.frequency.exponentialRampToValueAtTime(2800, time + duration);

    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.setValueAtTime(200, time);
    filter.frequency.exponentialRampToValueAtTime(5000, time + duration);
    filter.Q.value = 2.0;

    gain.gain.setValueAtTime(0.05, time);
    gain.gain.linearRampToValueAtTime(0.45, time + duration - 0.1);
    gain.gain.exponentialRampToValueAtTime(0.001, time + duration);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.samplerGainNode);

    osc.start(time);
    osc.stop(time + duration);
  }

  /** Tape Stop Turntable Power Off FX **/
  synthTapeStop(ctx, time) {
    const duration = 0.6;
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();

    osc1.type = "sawtooth";
    osc2.type = "square";

    osc1.frequency.setValueAtTime(650, time);
    osc1.frequency.exponentialRampToValueAtTime(15, time + duration);

    osc2.frequency.setValueAtTime(658, time);
    osc2.frequency.exponentialRampToValueAtTime(15, time + duration);

    gain.gain.setValueAtTime(0.4, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + duration);

    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(this.samplerGainNode);

    osc1.start(time);
    osc2.start(time);
    osc1.stop(time + duration);
    osc2.stop(time + duration);
  }

  /** Classic TR-808 Dual-Oscillator Cowbell **/
  synthCowbell(ctx, time) {
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc1.type = "square";
    osc2.type = "square";
    osc1.frequency.value = 540;
    osc2.frequency.value = 800;

    filter.type = "bandpass";
    filter.frequency.value = 850;
    filter.Q.value = 3.0;

    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(0.5, time + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.25);

    osc1.connect(filter);
    osc2.connect(filter);
    filter.connect(gain);
    gain.connect(this.samplerGainNode);

    osc1.start(time);
    osc2.start(time);
    osc1.stop(time + 0.25);
    osc2.stop(time + 0.25);
  }

  /** 4-Step Rhythmic Beat Fill Roll **/
  synthBeatRoll(ctx, time) {
    this.synthKick(ctx, time);
    this.synthSnare(ctx, time + 0.08);
    this.synthKick(ctx, time + 0.16);
    this.synthSnare(ctx, time + 0.24);
    this.synthHihat(ctx, time + 0.24);
  }

  /** Trap Resonant Rimshot Transient **/
  synthRimshot(ctx, time) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "triangle";
    osc.frequency.setValueAtTime(1600, time);
    osc.frequency.exponentialRampToValueAtTime(300, time + 0.015);

    gain.gain.setValueAtTime(0.6, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.04);

    osc.connect(gain);
    gain.connect(this.samplerGainNode);

    osc.start(time);
    osc.stop(time + 0.04);
  }

  /** Triplet Sub Kick Stutter Roll **/
  synthSubKickRoll(ctx, time) {
    this.synthKick(ctx, time);
    this.synthKick(ctx, time + 0.09);
    this.synthKick(ctx, time + 0.18);
  }

  synthCrash(ctx, time) {
    const bufferSize = ctx.sampleRate * 1.5;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = "highpass";
    filter.frequency.value = 7000;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.3, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 1.2);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.samplerGainNode);

    noise.start(time);
    noise.stop(time + 1.2);
  }

  synthClap(ctx, time) {
    const bufferSize = ctx.sampleRate * 0.5;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = 1200;
    filter.Q.value = 2.0;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, time);
    gain.gain.setValueAtTime(0.25, time);
    gain.gain.setValueAtTime(0.0, time + 0.01);
    gain.gain.setValueAtTime(0.25, time + 0.02);
    gain.gain.setValueAtTime(0.0, time + 0.03);
    gain.gain.setValueAtTime(0.25, time + 0.04);
    gain.gain.setValueAtTime(0.0, time + 0.05);
    gain.gain.setValueAtTime(0.35, time + 0.06);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.4);

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.samplerGainNode);

    noise.start(time);
    noise.stop(time + 0.4);
  }

  synthRumble(ctx, time) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "sine";
    osc.frequency.setValueAtTime(60, time);
    osc.frequency.linearRampToValueAtTime(42, time + 0.65);

    gain.gain.setValueAtTime(0, time);
    for (let t = 0; t < 0.6; t += 0.08) {
      gain.gain.setValueAtTime(0.4, time + t);
      gain.gain.setValueAtTime(0.0, time + t + 0.04);
    }
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.65);

    osc.connect(gain);
    gain.connect(this.samplerGainNode);

    osc.start(time);
    osc.stop(time + 0.65);
  }

  synthChirp(ctx, time) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "triangle";
    osc.frequency.setValueAtTime(450, time);
    osc.frequency.linearRampToValueAtTime(1700, time + 0.08);
    osc.frequency.linearRampToValueAtTime(250, time + 0.18);

    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(0.35, time + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.18);

    osc.connect(gain);
    gain.connect(this.samplerGainNode);

    osc.start(time);
    osc.stop(time + 0.18);
  }

  /** Synthesize custom sounds using oscillators to prevent loading external assets **/
  synthAirhorn(ctx, time) {
    // Airhorns are composed of multiple detuned sawtooth waves playing in bursts
    const freqs = [330, 392, 440, 493];
    const duration = 0.8;

    freqs.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sawtooth";
      osc.frequency.value = freq;
      // Detune a little bit to create beefy brass sound
      osc.detune.value = (idx - 1.5) * 20;

      // Envelope: volume gate
      gain.gain.setValueAtTime(0, time);
      gain.gain.linearRampToValueAtTime(0.25, time + 0.05); // quick attack
      // Rhythmic pulses of airhorn
      gain.gain.setValueAtTime(0.25, time + 0.25);
      gain.gain.setValueAtTime(0.0, time + 0.28);
      gain.gain.setValueAtTime(0.25, time + 0.32);
      gain.gain.setValueAtTime(0.25, time + 0.55);
      gain.gain.exponentialRampToValueAtTime(0.001, time + duration); // decay

      // Filter to sound like a megaphone/horn
      const filter = ctx.createBiquadFilter();
      filter.type = "bandpass";
      filter.frequency.value = 800;
      filter.Q.value = 1.5;

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(this.samplerGainNode);

      osc.start(time);
      osc.stop(time + duration);
    });
  }

  synthLaser(ctx, time) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "sawtooth";
    // Sweep frequency rapidly from high to low
    osc.frequency.setValueAtTime(2000, time);
    osc.frequency.exponentialRampToValueAtTime(80, time + 0.4);

    gain.gain.setValueAtTime(0.4, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.4);

    osc.connect(gain);
    gain.connect(this.samplerGainNode);

    osc.start(time);
    osc.stop(time + 0.4);
  }

  synthSubDrop(ctx, time) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "sine";
    // Sub bass sweep
    osc.frequency.setValueAtTime(100, time);
    osc.frequency.linearRampToValueAtTime(25, time + 1.2);

    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(0.6, time + 0.1);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 1.2);

    osc.connect(gain);
    gain.connect(this.samplerGainNode);

    osc.start(time);
    osc.stop(time + 1.2);
  }

  synthSiren(ctx, time) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "triangle";
    osc.frequency.setValueAtTime(400, time);
    
    // Waveform LFO sweeping frequency up and down
    osc.frequency.linearRampToValueAtTime(800, time + 0.3);
    osc.frequency.linearRampToValueAtTime(400, time + 0.6);
    osc.frequency.linearRampToValueAtTime(800, time + 0.9);
    osc.frequency.linearRampToValueAtTime(400, time + 1.2);
    osc.frequency.exponentialRampToValueAtTime(40, time + 1.5);

    gain.gain.setValueAtTime(0.4, time);
    gain.gain.setValueAtTime(0.4, time + 1.2);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 1.5);

    osc.connect(gain);
    gain.connect(this.samplerGainNode);

    osc.start(time);
    osc.stop(time + 1.5);
  }

  synthKick(ctx, time) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "sine";
    osc.frequency.setValueAtTime(150, time);
    osc.frequency.exponentialRampToValueAtTime(0.01, time + 0.25);

    gain.gain.setValueAtTime(1.0, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.25);

    osc.connect(gain);
    gain.connect(this.samplerGainNode);

    osc.start(time);
    osc.stop(time + 0.25);
  }

  synthSnare(ctx, time) {
    // Noise channel
    const bufferSize = ctx.sampleRate * 0.2; // 200ms
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }

    const noiseSource = ctx.createBufferSource();
    noiseSource.buffer = noiseBuffer;

    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = "highpass";
    noiseFilter.frequency.value = 1000;

    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.5, time);
    noiseGain.gain.exponentialRampToValueAtTime(0.01, time + 0.2);

    noiseSource.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(this.samplerGainNode);

    // Oscillator body
    const osc = ctx.createOscillator();
    const oscGain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.value = 180;

    oscGain.gain.setValueAtTime(0.5, time);
    oscGain.gain.exponentialRampToValueAtTime(0.01, time + 0.1);

    osc.connect(oscGain);
    oscGain.connect(this.samplerGainNode);

    noiseSource.start(time);
    osc.start(time);

    noiseSource.stop(time + 0.2);
    osc.stop(time + 0.2);
  }

  synthHihat(ctx, time) {
    const bufferSize = ctx.sampleRate * 0.05; // 50ms short burst
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }

    const source = ctx.createBufferSource();
    source.buffer = noiseBuffer;

    const filter = ctx.createBiquadFilter();
    filter.type = "highpass";
    filter.frequency.value = 7000; // super high crisp hiss

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.3, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.05);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.samplerGainNode);

    source.start(time);
    source.stop(time + 0.05);
  }

  synthNoiseClap(ctx, time) {
    const bufferSize = ctx.sampleRate * 0.4; // 400ms
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }

    const source = ctx.createBufferSource();
    source.buffer = noiseBuffer;

    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = 1200;
    filter.Q.value = 1.0;

    const gain = ctx.createGain();
    
    // Clap is composed of multiple quick stutter impulses before the final release
    gain.gain.setValueAtTime(0.6, time);
    gain.gain.linearRampToValueAtTime(0.01, time + 0.02);
    
    gain.gain.setValueAtTime(0.5, time + 0.03);
    gain.gain.linearRampToValueAtTime(0.01, time + 0.05);
    
    gain.gain.setValueAtTime(0.4, time + 0.06);
    gain.gain.linearRampToValueAtTime(0.01, time + 0.08);

    gain.gain.setValueAtTime(0.6, time + 0.09);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.4);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.samplerGainNode);

    source.start(time);
    source.stop(time + 0.4);
  }

  // ==========================================
  // MASTER RECORDING CONTROLS
  // ==========================================

  startRecording() {
    if (this.isRecording || !this.masterGainNode) return false;

    this.initialize();
    const ctx = this.audioContext;
    
    try {
      this.recordedChunks = [];
      const dest = ctx.createMediaStreamDestination();
      
      // Connect master output to recorder destination
      this.masterGainNode.connect(dest);

      this.mediaRecorder = new MediaRecorder(dest.stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm"
      });

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      this.mediaRecorder.start(100); // 100ms chunks
      this.isRecording = true;
      this.recordingStartTime = Date.now();
      this.recordingElapsedTime = 0;

      // Start duration tracker
      this.recordingInterval = setInterval(() => {
        this.recordingElapsedTime = Math.floor((Date.now() - this.recordingStartTime) / 1000);
      }, 1000);

      return true;
    } catch (e) {
      console.error("Failed to start MediaRecorder", e);
      return false;
    }
  }

  stopRecording() {
    if (!this.isRecording || !this.mediaRecorder) return Promise.resolve(null);

    if (this.recordingInterval) {
      clearInterval(this.recordingInterval);
      this.recordingInterval = null;
    }

    return new Promise((resolve) => {
      this.mediaRecorder.onstop = async () => {
        const webmBlob = new Blob(this.recordedChunks, { type: "audio/webm" });
        this.isRecording = false;

        // MediaRecorder cannot produce MP3 in any browser — it only offers WebM/Opus
        // (and MP4/AAC on Safari). So the capture stays WebM and we decode it to PCM
        // and re-encode to MP3 here, off the main thread.
        try {
          const arrayBuffer = await webmBlob.arrayBuffer();
          const decoded = await this.audioContext.decodeAudioData(arrayBuffer);

          try {
            const mp3Blob = await audioBufferToMp3(decoded, {
              kbps: this.recordingBitrate,
              onProgress: (pct) => {
                if (this.onRecordingEncodeProgress) this.onRecordingEncodeProgress(pct);
              }
            });
            const url = URL.createObjectURL(mp3Blob);
            resolve({
              blob: mp3Blob,
              url,
              duration: this.recordingElapsedTime,
              format: "mp3",
              extension: "mp3"
            });
          } catch (mp3Error) {
            // Encoder unavailable or an unencodable sample rate — ship lossless WAV
            // rather than losing the take.
            console.warn("MP3 encoding failed, exporting WAV instead", mp3Error);
            const wavBlob = audioBufferToWav(decoded);
            const url = URL.createObjectURL(wavBlob);
            resolve({
              blob: wavBlob,
              url,
              duration: this.recordingElapsedTime,
              format: "wav",
              extension: "wav"
            });
          }
        } catch (e) {
          console.warn("Decode failed, exporting raw webm", e);
          const url = URL.createObjectURL(webmBlob);
          resolve({
            blob: webmBlob,
            url,
            duration: this.recordingElapsedTime,
            format: "webm",
            extension: "webm"
          });
        }
      };

      if (this.mediaRecorder.state !== "inactive") {
        try {
          this.mediaRecorder.requestData();
        } catch (e) {}
      }
      this.mediaRecorder.stop();
      this.mediaRecorder = null;
    });
  }

  // ==========================================
  // TEARDOWN
  // ==========================================

  destroy() {
    this.stopRecording();
    this.stopMicrophone();
    // Must run before the context is closed: a surviving devicechange listener would
    // otherwise fire recovery against a closed context.
    this.removeMicRecoveryListeners();
    this.onMicHealthChange = null;
    this.onMicFeedbackGuard = null;

    // Destroy all decks
    const deckIds = Array.from(this.decks.keys());
    deckIds.forEach(id => this.destroyDeck(id));

    if (this.masterGainNode) {
      try { this.masterGainNode.disconnect(); } catch (e) {}
    }
    if (this.masterCompressorNode) {
      try { this.masterCompressorNode.disconnect(); } catch (e) {}
    }
    if (this.crossfadeLeftGain) {
      try { this.crossfadeLeftGain.disconnect(); } catch (e) {}
    }
    if (this.crossfadeRightGain) {
      try { this.crossfadeRightGain.disconnect(); } catch (e) {}
    }
    if (this.samplerGainNode) {
      try { this.samplerGainNode.disconnect(); } catch (e) {}
    }

    if (this.audioContext && this.audioContext.state !== "closed") {
      this.audioContext.close();
    }
    this.audioContext = null;
  }
}
